import { AppError, supabase } from '@/services/supabase/client'
import { usernameAvailable } from '@/services/api/social'

const USERNAME_RE = /^[a-z0-9_]{3,20}$/

export function validateUsername(raw: string): string | null {
  const value = raw.trim().toLowerCase()
  if (value.length < 3) return 'Al menos 3 caracteres.'
  if (value.length > 20) return 'Como máximo 20 caracteres.'
  if (!USERNAME_RE.test(value)) return 'Solo letras minúsculas, números y guion bajo.'
  return null
}

export function validatePassword(value: string): string | null {
  if (value.length < 8) return 'Al menos 8 caracteres.'
  if (!/[a-zA-Z]/.test(value) || !/[0-9]/.test(value)) return 'Incluye letras y números.'
  return null
}

export function validateEmail(value: string): string | null {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())) return 'Ese correo no parece válido.'
  return null
}

export async function checkUsername(username: string): Promise<boolean> {
  try {
    return await usernameAvailable(username.trim().toLowerCase())
  } catch {
    return true // never block signup on a transient check
  }
}

export async function signIn(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  })
  if (error) {
    // GoTrue only says this AFTER the password checked out, so it reveals
    // nothing to guessers — and the honest answer unblocks a real newcomer.
    const code = 'code' in error ? String(error.code) : ''
    if (code === 'email_not_confirmed' || /not confirmed/i.test(error.message ?? '')) {
      throw new AppError('email_unconfirmed', 'Esta cuenta aún no está confirmada.')
    }
    // Deliberately vague: a precise answer here tells an attacker which
    // addresses have accounts. (§110)
    throw new AppError('invalid_credentials', 'Correo o contraseña incorrectos.')
  }
}

export async function signUp(input: {
  email: string
  password: string
  username: string
  displayName: string
}): Promise<{ needsConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      // The database trigger reads these and creates the profile inside the
      // same transaction, so a taken username fails the signup cleanly.
      data: {
        username: input.username.trim().toLowerCase(),
        display_name: input.displayName.trim(),
      },
      emailRedirectTo: window.location.origin + '/entrar',
    },
  })

  if (error) {
    const message = error.message ?? ''
    if (message.includes('username_reserved')) {
      throw new AppError('username_reserved', 'Ese nombre de usuario no está disponible.')
    }
    if (message.includes('duplicate key') || message.includes('profiles_username_key')) {
      throw new AppError('username_taken', 'Ese nombre de usuario ya está cogido.')
    }
    if (message.toLowerCase().includes('already registered')) {
      throw new AppError('email_taken', 'Ya existe una cuenta con ese correo.')
    }
    if (error.status === 429 || /rate limit/i.test(message)) {
      throw new AppError(
        'rate_limited',
        'Ahora mismo se está registrando mucha gente. Espera unos minutos y vuelve a intentarlo.',
      )
    }
    throw new AppError('signup_failed', 'No se ha podido crear la cuenta.')
  }

  // With confirmations on, GoTrue answers an already-confirmed address with a
  // ghost user carrying no identities instead of an error.
  if (data.user && (data.user.identities?.length ?? 0) === 0) {
    throw new AppError('email_taken', 'Ya existe una cuenta con ese correo.')
  }

  return { needsConfirmation: !data.session }
}

export async function resendConfirmation(email: string) {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.trim(),
    options: { emailRedirectTo: window.location.origin + '/entrar' },
  })
  if (error) {
    if (error.status === 429 || /rate|seconds/i.test(error.message ?? '')) {
      throw new AppError('rate_limited', 'Dale un momento antes de pedir otro correo.')
    }
    throw new AppError('resend_failed', 'No se ha podido reenviar el correo.')
  }
}

export async function signOut() {
  await supabase.auth.signOut()
}

export async function requestPasswordReset(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: window.location.origin + '/nueva-contrasena',
  })
  // Always report success: whether an address has an account is not public.
  if (error && !error.message.includes('rate')) return
}

export async function updatePassword(password: string) {
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw new AppError('password_failed', 'No se ha podido cambiar la contraseña.')
}
